import Editor from './Editor.js'
import { collectSvgMetrics } from './svgMetrics.js'
import { textElementToPathData } from './textToPath.js'
import { initTouchGestures } from './touchGestures.js'

let svgEditorModule;

async function waitForFontsReady (timeoutMs = 3000) {
  try {
    if (document.fonts && document.fonts.ready) {
      await Promise.race([
        document.fonts.ready,
        new Promise((resolve) => setTimeout(resolve, timeoutMs))
      ])
    }
  } catch (_) {
    // ignore — proceed with best effort
  }
  // Extra settle time for layout/paint after any late font swap
  await new Promise((resolve) =>
    requestAnimationFrame(() => requestAnimationFrame(resolve))
  )
}

// A data:-URI SVG rendered via <img> runs in a sandboxed "image" context that fundamentally
// does not process @font-face at all — confirmed by testing a minimal, fully self-contained SVG
// with an embedded (data: URI) @font-face: it still renders in the browser's default serif
// fallback. This is a platform limitation, not something fixable by embedding font bytes.
// The actual fix: convert <text> elements to <path> geometry before rasterizing (reusing the
// app's existing opentype.js-based textToPath converter, already used for xTool/laser export) —
// paths are plain vector shapes with no font dependency, so they rasterize correctly regardless
// of image-mode sandboxing, the same way the rest of the artwork already does.
async function convertTextElementsToPaths (parsedSvg) {
  const textElements = Array.from(parsedSvg.querySelectorAll('text'))
  for (const textEl of textElements) {
    try {
      const d = await textElementToPathData(textEl)
      if (!d) {
        textEl.remove() // couldn't convert — omit rather than show it in the wrong font
        continue
      }
      const pathEl = parsedSvg.ownerDocument.createElementNS('http://www.w3.org/2000/svg', 'path')
      pathEl.setAttribute('d', d)
      for (const attr of ['fill', 'fill-opacity', 'stroke', 'stroke-width', 'stroke-opacity', 'transform', 'opacity']) {
        const value = textEl.getAttribute(attr)
        if (value !== null) pathEl.setAttribute(attr, value)
      }
      textEl.replaceWith(pathEl)
    } catch (err) {
      console.error('convertTextElementsToPaths: failed to convert a text element, omitting it:', err)
      textEl.remove()
    }
  }
}

window.ReihaxSvgEditor = {
  _isSeller: false,
  _documentTitle: 'export',
  initialize: async function (containerDiv, canvasWidth, canvasHeight, contourWidth, contourHeight, contourUnit, title, svg64encoded,
    backgroundContour64Encoded, backgroundContourFileExtension, maxObjectsCount, costPerObject, currency, isBlackWhite, scalingFactor, specifyMaterials, materialColors, restrictedPaletteColors,
    isSeller) {
    if (!document.getElementById('reihax-google-fonts')) {
      const link = document.createElement('link')
      link.id = 'reihax-google-fonts'
      link.rel = 'stylesheet'
      link.href = 'https://fonts.googleapis.com/css2?family=Roboto:wght@400;700&family=Open+Sans:wght@400;700&family=Lato:wght@400;700&family=Montserrat:wght@400;700&family=Poppins:wght@400;700&family=Oswald:wght@400;700&family=Raleway:wght@400;700&family=Nunito:wght@400;700&family=Inter:wght@400;700&family=Ubuntu:wght@400;700&family=PT+Sans:wght@400;700&family=Josefin+Sans:wght@400;700&family=Barlow:wght@400;700&family=Exo+2:wght@400;700&family=Playfair+Display:wght@400;700&family=Merriweather:wght@400;700&family=Lora:wght@400;700&family=PT+Serif:wght@400;700&family=Libre+Baskerville:wght@400;700&family=EB+Garamond:wght@400;700&family=Cormorant+Garamond:wght@400;700&family=Cinzel:wght@400;700&family=Bebas+Neue&family=Anton&family=Righteous&family=Bangers&family=Russo+One&family=Permanent+Marker&family=Alfa+Slab+One&family=Black+Ops+One&family=Dancing+Script:wght@400;700&family=Pacifico&family=Great+Vibes&family=Sacramento&family=Satisfy&family=Caveat:wght@400;700&family=Lobster&family=Kaushan+Script&family=Roboto+Mono:wght@400;700&family=Source+Code+Pro:wght@400;700&family=Space+Mono:wght@400;700&family=Inconsolata:wght@400;700&family=Fira+Code:wght@400;700&family=Amiri:wght@400;700&family=Scheherazade+New:wght@400;700&family=Reem+Kufi:wght@400;700&family=Cairo:wght@400;700&family=Tajawal:wght@400;700&family=UnifrakturMaguntia&family=Pirata+One&family=Uncial+Antiqua&family=IM+Fell+English&family=Almendra:wght@400;700&family=Metamorphous&family=Cinzel+Decorative:wght@400;700&display=swap'
      document.head.appendChild(link)
    }
    console.log('Entering ReihaxSvgEditor.initialize');
    console.log('canvasWidth (real-world units):', canvasWidth); // e.g., 20 (cm)
    console.log('canvasHeight (real-world units):', canvasHeight); // e.g., 10 (cm)

    // console.log('scalingFactor', scalingFactor);

    // Apply the scaling to the contour dimensions
    // const scaledContourWidth = contourWidth * scalingFactor;
    // const scaledContourHeight = contourHeight * scalingFactor;

    console.log('contourWidth:', contourWidth);
    console.log('contourHeight:', contourHeight);
    console.log('contourUnit:', contourUnit);
    console.log('specifyMaterials:', specifyMaterials);
    console.log('materialColors:', materialColors);

    // Shape is the single source of truth.
    // SVG-Edit's internal coordinate system is pixels ("user units").
    // To keep rulers/doc properties aligned with the shape's real-world units,
    // we configure dimensions with unit-bearing strings (e.g., "100mm").
    // svgCanvas.setResolution() will convert these to px internally using its unit map.
    const unit = (contourUnit || 'cm').toString().trim().toLowerCase();
    const contourW = Number(contourWidth);
    const contourH = Number(contourHeight);
    const documentWidth = `${contourW}${unit}`;
    const documentHeight = `${contourH}${unit}`;

    svgEditorModule = svgEditorModule || {};
    // Persist seller flag and document title for xTool export extension.
    window.ReihaxSvgEditor._isSeller = isSeller === true
    window.ReihaxSvgEditor._documentTitle = title || 'export'
    // Persist shape dimensions (source of truth) for later serialization.
    svgEditorModule.shape = {
      contourWidth: contourW,
      contourHeight: contourH,
      contourUnit: unit,
      documentWidth,
      documentHeight,
      // Will be populated after init/enforcement with px values
      documentWidthPx: null,
      documentHeightPx: null
    }
    svgEditorModule.svgEditor = new Editor(containerDiv, documentWidth, documentHeight, title, svg64encoded,
      backgroundContour64Encoded, backgroundContourFileExtension, maxObjectsCount, costPerObject, currency, isBlackWhite, specifyMaterials, materialColors, restrictedPaletteColors);
    // A phone has no room for rulers (svgedit.css hides them there), and redrawing them on every step of
    // a pinch is work for nothing. Decided once, here: init() reads the setting when it builds the layout.
    if (window.matchMedia('(max-width: 700px), (max-height: 500px) and (pointer: coarse)').matches) {
      svgEditorModule.svgEditor.configObj.curConfig.showRulers = false
    }
    // Editor initialization is async; wait until svgCanvas is created before configuring/enforcing dimensions.
    await svgEditorModule.svgEditor.init();
    // Two fingers zoom and pan; one finger keeps drawing (see touchGestures.js).
    initTouchGestures(svgEditorModule.svgEditor)

    // Apply scaling factor to the canvas dimensions to fit the viewport
    // const scaledCanvasWidth = canvasWidth * scalingFactor;
    // const scaledCanvasHeight = canvasHeight * scalingFactor;

    svgEditorModule.svgEditor.setConfig({
      dimensions: [documentWidth, documentHeight], // DOCUMENT PROPERTIES
      // Shape dimensions are the single source of truth; do not let loaded SVG override the page size.
      allowInitialUserOverride: false,
      extensions: [],
      noDefaultExtensions: false,
      userExtensions: [],
      // Keep editor units aligned with the shape unit so doc properties match the shape.
      baseUnit: unit,
      // Unique localStorage key per canvas size so content from one canvas
      // (e.g. 599cm) is never restored on a different-sized canvas (e.g. 599mm).
      canvasName: `reihax-${contourW}${unit}`,
    });

    // SVG-Edit also persists editor options (including base unit) in localStorage as preferences.
    // If the user previously used a different unit (e.g. px), it can override the intended
    // unit display on subsequent loads. Force the preference to the shape unit so rulers and
    // document properties stay consistent after saving and reopening.
    try {
      if (svgEditorModule?.svgEditor?.configObj?.pref) {
        svgEditorModule.svgEditor.configObj.pref('baseUnit', unit);
      }
      if (svgEditorModule?.svgEditor?.configObj?.curConfig && svgEditorModule?.svgEditor?.svgCanvas?.setConfig) {
        svgEditorModule.svgEditor.configObj.curConfig.baseUnit = unit;
        svgEditorModule.svgEditor.svgCanvas.setConfig(svgEditorModule.svgEditor.configObj.curConfig);
      }
    } catch (e) {
      console.warn('Failed to force baseUnit preference to shape unit', e);
    }

    // Ensure current document (including any preloaded SVG) is fitted into the shape-sized page.
    // (Editor.loadSvgString() also calls this after successful open.)
    try {
      if (svgEditorModule?.svgEditor?.enforceDocumentDimensionsAndFitContent) {
        svgEditorModule.svgEditor.enforceDocumentDimensionsAndFitContent();
      }
      // Persist the effective (px) document size after enforcement; used for export normalization.
      const res = svgEditorModule?.svgEditor?.svgCanvas?.getResolution && svgEditorModule.svgEditor.svgCanvas.getResolution();
      if (res && svgEditorModule.shape) {
        svgEditorModule.shape.documentWidthPx = res.w;
        svgEditorModule.shape.documentHeightPx = res.h;
        // Default text font size: ~1/5 of the shorter canvas dimension in px
        try {
          const defaultFontSize = Math.round(res.w / 10)
          svgEditorModule.svgEditor.svgCanvas.setCurText('font_size', defaultFontSize)
        } catch (_) {}
      }
    } catch (e) {
      console.warn('Failed to enforce shape-sized document and fit content', e);
    }

    // // Decode the contour SVG if provided and add it to the editor
    // if (contourSvg64encoded) {
    //   const svgXml = atob(contourSvg64encoded); // Decode the base64 SVG
    //   const parser = new DOMParser();
    //   const svgDoc = parser.parseFromString(svgXml, "image/svg+xml");
    //   const contourSvgElement = svgDoc.documentElement;
    //
    //   const canvasElement = document.getElementById('svgcanvas');
    //   if (canvasElement) {
    //     // Append the decoded contour SVG to the canvas
    //     canvasElement.appendChild(contourSvgElement);
    //   }
    // }

    // Apply scaling factor to set initial contour viewBox
    // Store current viewBox for future zooms
    svgEditorModule.currentViewBox = {
      x: 0,
      y: 0,
      width: canvasWidth,
      height: canvasHeight
    };

    // Method to apply zoom
    svgEditorModule.zoom = function (zoomFactor) {
      // Update viewBox to zoom in or out
      svgEditorModule.currentViewBox.width /= zoomFactor;
      svgEditorModule.currentViewBox.height /= zoomFactor;

      // Update the viewBox string and set it
      const newViewBoxStr = `${svgEditorModule.currentViewBox.x} ${svgEditorModule.currentViewBox.y} ${svgEditorModule.currentViewBox.width} ${svgEditorModule.currentViewBox.height}`;
      svgEditorModule.svgEditor.svgCanvas.setSvgString(
        `<svg viewBox="${newViewBoxStr}" width="${canvasWidth}" height="${canvasHeight}"></svg>`
      );
    };


    if (isBlackWhite) {
      svgEditorModule.svgEditor.configObj.curConfig.initFill.color = 'none';
    }

    // Set default fill color to first material color if materials are specified
    if (specifyMaterials && materialColors && materialColors.length > 0) {
      const firstColor = materialColors[0].colorHex;
      // Remove # if present
      const colorWithoutHash = firstColor.startsWith('#') ? firstColor.substring(1) : firstColor;
      svgEditorModule.svgEditor.configObj.curConfig.initFill.color = colorWithoutHash;
      // initFill is only read when the canvas is constructed, which has already happened by now,
      // so the line above alone changes nothing. The canvas keeps its own copies of the style a
      // new object starts with — and text has its own, hardcoded to black (#000000), which the
      // cart then rejects as "not one of the seller's materials".
      const canvas = svgEditorModule.svgEditor.svgCanvas;
      const rgb = '#' + colorWithoutHash.substring(0, 6); // drop an alpha pair if the hex has one
      canvas.setCurShape('fill', rgb);
      canvas.setCurText('fill', rgb);
      console.log('Set default fill color to:', colorWithoutHash);
    }
  },
  getSvgContent64Encoded: function () {
    const svg = svgEditorModule.svgEditor.svgCanvas.getSvgString();
    const normalizedSvg = this.normalizeSvgRootToShape(svg);
    const b64Data = svgEditorModule.svgEditor.svgCanvas.encode64(normalizedSvg);
    //console.log(b64Data);
    return b64Data;
  },
  getObjectsCount: function () {
    const svgContent = svgEditorModule.svgEditor.svgCanvas.getSvgContent();
    //console.log(svgContent);
    let childs = svgContent.getElementsByTagName('*');
    let objectCount = childs.length - 2;
    console.log(objectCount);
    return objectCount;
  },
  /**
   * Called when the buyer/seller edits the physical-size (contour) fields after the editor
   * has already been created. Does NOT re-run `initialize` / recreate the canvas — that would
   * destroy any in-progress artwork. Updates the page size in place and keeps `shape` (the
   * source of truth used by normalizeSvgRootToShape on save) in sync with the new dimensions.
   */
  setContour: function (width, height, unit) {
    try {
      if (!svgEditorModule || !svgEditorModule.svgEditor) return
      const u = (unit || 'cm').toString().trim().toLowerCase()
      const w = Number(width)
      const h = Number(height)
      if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return

      svgEditorModule.svgEditor.setContourDimensions(w, h, u)

      if (svgEditorModule.shape) {
        svgEditorModule.shape.contourWidth = w
        svgEditorModule.shape.contourHeight = h
        svgEditorModule.shape.contourUnit = u
        svgEditorModule.shape.documentWidth = `${w}${u}`
        svgEditorModule.shape.documentHeight = `${h}${u}`

        const res = svgEditorModule.svgEditor.svgCanvas.getResolution && svgEditorModule.svgEditor.svgCanvas.getResolution()
        if (res) {
          svgEditorModule.shape.documentWidthPx = res.w
          svgEditorModule.shape.documentHeightPx = res.h
        }
      }
    } catch (e) {
      console.warn('ReihaxSvgEditor.setContour failed', e)
    }
  },
  getSvgContentResized64Encoded: function (percentage) {
    //let resized = svgEditorModule.svgEditor.getSvgContentResized(percentage);
    //console.log('resized: ' + resized);
    //return resized;
    return svgEditorModule.svgEditor.getSvgContentResized(percentage);
  },
  getThumbnailImage64Encoded: async function () {
    // Previously used html2canvas, which clones the *entire live document* into a detached
    // iframe to render. This app has custom elements whose attributeChangedCallback throws
    // when cloned that way (confirmed via a real uncaught pageerror during testing:
    // "TypeError: Cannot read properties of null (reading 'getElementById')" inside
    // DocumentCloner.createElementClone). That error fires as a genuine uncaught global
    // error — outside any promise chain — so no try/catch or timeout here could ever catch
    // it, which is what left Save permanently stuck before MudDialog.Close() on real pages.
    // Rasterizing via plain Image + <canvas> avoids DOM cloning entirely: it only ever loads
    // the already-serialized SVG string as image data, so there's nothing of the live page
    // for a broken custom element to interfere with.
    try {
      // Rasterize the same clean, correctly-sized SVG serialization already used for
      // Svg64/Resized64 (svgToString), NOT the live svgcontent DOM node directly — the
      // live node's width/height attributes are mutated by Editor.js#updateCanvas to
      // reflect the CURRENT ZOOM LEVEL (contentW * zoom * canvas_expansion), so cloning
      // it verbatim bakes in whatever pan/zoom state was active when the seller hit
      // Save, producing a cropped/zoomed thumbnail instead of the full artwork.
      const svgCanvas = svgEditorModule.svgEditor.svgCanvas
      const svgString = svgCanvas.svgToString(svgCanvas.getSvgContent(), 0)
      if (!svgString) return ''

      await waitForFontsReady()

      const parsedSvg = new DOMParser().parseFromString(svgString, 'image/svg+xml').documentElement

      // Text-to-path conversion loads opentype.js + font files from a CDN on first use — not
      // bounded by the image-load timeout below, so race it against a timeout of its own. Worst
      // case on timeout: whatever text elements hadn't converted yet are left as <text> (same
      // font-mismatch as before this fix for those specific elements), but Save always completes.
      await Promise.race([
        convertTextElementsToPaths(parsedSvg),
        new Promise((resolve) => setTimeout(resolve, 6000))
      ])

      // The SVG's width/height attributes are in the design's real-world unit (e.g. mm for a
      // 100mm x 110mm shape), NOT pixels — using them directly as the raster canvas size produces
      // a ~100x110px canvas that then gets stretched up to display size, i.e. a blocky/pixelated
      // thumbnail. Get the unit-independent aspect ratio from the viewBox instead, and rasterize
      // at a fixed pixel resolution regardless of what physical size the artwork represents.
      const viewBox = (parsedSvg.getAttribute('viewBox') || '').trim().split(/\s+/).map(Number)
      const aspectW = viewBox.length === 4 && viewBox[2] > 0 ? viewBox[2] : (parseInt(parsedSvg.getAttribute('width'), 10) || 1)
      const aspectH = viewBox.length === 4 && viewBox[3] > 0 ? viewBox[3] : (parseInt(parsedSvg.getAttribute('height'), 10) || 1)
      const targetLongSide = 500 // rasterize well above the 250x210 display size for a crisp result
      const scale = targetLongSide / Math.max(aspectW, aspectH)
      const w = Math.max(1, Math.round(aspectW * scale))
      const h = Math.max(1, Math.round(aspectH * scale))

      // btoa only handles latin1/binary strings — encodeURIComponent/unescape is the
      // standard idiom for safely base64-encoding an arbitrary UTF-8 string with it.
      const finalSvgString = new XMLSerializer().serializeToString(parsedSvg)
      const svgDataUrl = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(finalSvgString)))

      const img = new Image()
      const loadPromise = new Promise((resolve, reject) => {
        img.onload = () => resolve()
        img.onerror = () => reject(new Error('thumbnail source image failed to load'))
      })
      const timeoutPromise = new Promise((_resolve, reject) =>
        setTimeout(() => reject(new Error('thumbnail render timed out')), 8000)
      )
      img.src = svgDataUrl
      await Promise.race([loadPromise, timeoutPromise])

      const canvas = document.createElement('canvas')
      canvas.width = w
      canvas.height = h
      const ctx = canvas.getContext('2d')
      ctx.fillStyle = '#ffffff'
      ctx.fillRect(0, 0, w, h)
      ctx.drawImage(img, 0, 0, w, h)

      // WebP compresses noticeably smaller than PNG for this kind of flat-color artwork, which
      // lets us rasterize at a higher resolution (crisper thumbnail) without a bigger payload.
      // All target browsers support canvas WebP export; the C# side (DesignDetailStep.razor)
      // assumes this Thumbnail64 is WebP and wraps it as data:image/webp — keep both in sync.
      const dataUrl = canvas.toDataURL('image/webp', 0.9)
      return dataUrl.substring(dataUrl.indexOf(',') + 1)
    } catch (err) {
      console.error('getThumbnailImage64Encoded failed:', err)
      return ''
    }
  },
  getMetrics: function (pricingComplexity) {
    console.log('pricingComplexity passed to index.js', pricingComplexity);
    return collectSvgMetrics(svgEditorModule.svgEditor, pricingComplexity);
  },
  get _shape () { return svgEditorModule && svgEditorModule.shape },
  _userDesignsRef: null,
  setUserDesignsRef: function (dotnetRef) {
    window.ReihaxSvgEditor._userDesignsRef = dotnetRef
  },
  fitToCanvas: function () {
    try {
      if (!svgEditorModule || !svgEditorModule.svgEditor) return;
      // Defer to the next animation frames so the workarea has been laid out;
      // zoomChanged reads the workarea's computed size to compute the fit zoom.
      const doFit = () => {
        try {
          if (!svgEditorModule || !svgEditorModule.svgEditor) return;
          svgEditorModule.svgEditor.zoomChanged(window, 'canvas');
        } catch (e) {
          console.warn('ReihaxSvgEditor.fitToCanvas failed', e);
        }
      };
      if (typeof window.requestAnimationFrame === 'function') {
        window.requestAnimationFrame(() => window.requestAnimationFrame(doFit));
      } else {
        doFit();
      }
    } catch (e) {
      console.warn('ReihaxSvgEditor.fitToCanvas failed', e);
    }
  },
  exitTextEditMode: function () {
    try {
      if (!svgEditorModule || !svgEditorModule.svgEditor) return;
      const editor = svgEditorModule.svgEditor;
      const sc = editor.svgCanvas;
      if (!sc) return;
      const mode = sc.getMode && sc.getMode();
      if (mode === 'textedit') {
        // Attempt to commit/exit text editing cleanly
        if (sc.textActions) {
          if (typeof sc.textActions.toSelectMode === 'function') {
            sc.textActions.toSelectMode();
          }
          if (typeof sc.textActions.clear === 'function') {
            sc.textActions.clear();
          }
        }
        if (editor.leftPanel && typeof editor.leftPanel.clickSelect === 'function') {
          editor.leftPanel.clickSelect();
        } else if (typeof sc.setMode === 'function') {
          sc.setMode('select');
        }
      }
      // Ensure any focused input is blurred
      if (document.activeElement) {
        document.activeElement.blur();
      }
    } catch (e) {
      console.warn('ReihaxSvgEditor.exitTextEditMode failed', e);
    }
  },
  /**
   * Ensures exported SVG reflects the shape as the single source of truth.
   * Keeps internal editing in the shape's unit-space, and writes physical size with that unit.
   *
   * @param {string} svgString
   * @returns {string}
   */
  normalizeSvgRootToShape: function (svgString) {
    try {
      const shape = svgEditorModule && svgEditorModule.shape
      if (!shape || shape.contourWidth == null || shape.contourHeight == null || !shape.contourUnit || !shape.documentWidth || !shape.documentHeight) {
        return svgString
      }

      const parser = new DOMParser()
      const doc = parser.parseFromString(svgString, 'image/svg+xml')
      const root = doc.documentElement
      if (!root || root.nodeName.toLowerCase() !== 'svg') {
        return svgString
      }

      // Physical size (what you expect to see in the serialized SVG)
      root.setAttribute('width', `${shape.contourWidth}${shape.contourUnit}`)
      root.setAttribute('height', `${shape.contourHeight}${shape.contourUnit}`)

      // Mark as authored/normalized by our integration so we can avoid re-fitting
      // already-correct templates on reopen (which can otherwise introduce drift).
      root.setAttribute('data-reihax-authored', '1')
      root.setAttribute('data-reihax-shape', `${shape.documentWidth}x${shape.documentHeight}`)

      // Coordinate system remains px-based (SVG-Edit internal user units)
      // so editing stays consistent regardless of physical units.
      const pxW = Number(shape.documentWidthPx)
      const pxH = Number(shape.documentHeightPx)
      if (Number.isFinite(pxW) && Number.isFinite(pxH) && pxW > 0 && pxH > 0) {
        root.setAttribute('viewBox', `0 0 ${pxW} ${pxH}`)
      }

      // Some editors add an `xmlns:svg`; keep root namespace clean.
      if (!root.getAttribute('xmlns')) {
        root.setAttribute('xmlns', 'http://www.w3.org/2000/svg')
      }

      return new XMLSerializer().serializeToString(doc)
    } catch (e) {
      console.warn('normalizeSvgRootToShape failed', e)
      return svgString
    }
  },
  // (legacy px conversion helpers removed; editor now works in shape unit-space)
};
